'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import styles from './realty-header.module.css';

export const REALTY_HEADER_STICKY_CHANGE_EVENT = 'eliadmin:realty-header-sticky-change';

export type RealtyHeaderStickyChangeDetail = {
  sticky: boolean;
};

export type RealtyHeaderShellProps = {
  topRow: ReactNode;
  mainRow: ReactNode;
  categories: ReactNode;
  mobileBar: ReactNode;
  className?: string;
  desktopClassName?: string;
  topRowClassName?: string;
  mainRowClassName?: string;
  mobileBarClassName?: string;
  style?: CSSProperties;
};

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

export function RealtyHeaderShell({
  topRow,
  mainRow,
  categories,
  mobileBar,
  className,
  desktopClassName,
  topRowClassName,
  mainRowClassName,
  mobileBarClassName,
  style,
}: RealtyHeaderShellProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef(false);
  const thresholdRef = useRef(240);
  const frameRef = useRef<number | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    const emitStickyChange = (sticky: boolean) => {
      document.dispatchEvent(
        new CustomEvent<RealtyHeaderStickyChangeDetail>(REALTY_HEADER_STICKY_CHANGE_EVENT, {
          detail: { sticky },
        }),
      );
    };

    const updateThreshold = () => {
      const root = rootRef.current;
      if (!root || !mediaQuery.matches) return;

      thresholdRef.current = root.getBoundingClientRect().top + window.scrollY + root.offsetHeight;
    };

    const updateSticky = () => {
      frameRef.current = null;
      const nextSticky = mediaQuery.matches && window.scrollY >= thresholdRef.current;

      if (stickyRef.current === nextSticky) return;

      stickyRef.current = nextSticky;
      setIsSticky(nextSticky);
      emitStickyChange(nextSticky);
    };

    const scheduleUpdate = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(updateSticky);
    };

    const handleViewportChange = () => {
      updateThreshold();
      scheduleUpdate();
    };

    updateThreshold();
    updateSticky();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', handleViewportChange);
    mediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', handleViewportChange);
      mediaQuery.removeEventListener('change', handleViewportChange);

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }

      if (stickyRef.current) {
        emitStickyChange(false);
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, 'ea-header-main', className)}
      data-eli-component="realty-header"
      data-sticky={isSticky ? 'true' : 'false'}
      style={style}
    >
      <div className={cn(styles.desktop, desktopClassName)} data-eli-part="desktop-header">
        <div className={cn(styles.topRow, topRowClassName)} data-eli-part="top-row">
          {topRow}
        </div>
        <div className={styles.mainSlot} data-header-main-slot="true">
          <div
            className={cn(styles.mainViewport, isSticky && styles.mainViewportSticky)}
            data-header-main-sticky={isSticky ? 'true' : 'false'}
            data-eli-part="main-viewport"
          >
            <div className={cn(styles.mainRow, mainRowClassName)} data-eli-part="main-row">
              {mainRow}
            </div>
          </div>
        </div>
        {categories}
      </div>
      <div className={cn(styles.mobileBar, mobileBarClassName)} data-eli-part="mobile-bar">
        {mobileBar}
      </div>
    </div>
  );
}
